/**
 * Shared sticky-notes board — one per session, visible to and editable by every
 * user viewing that session_id. Independent of the roster's own edit history.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '@/api/client';
import { ConfirmDialog } from './ConfirmDialog';
import { ViewOnlyBanner } from './ViewOnlyBanner';
import { useSession } from '@/contexts/SessionContext';
import { dropNoteText, flushNoteTexts, hasUnsavedNoteText, NOTE_MAX_LENGTH, scheduleNoteText, type NoteSaveError } from '@/utils/noteSaves';
import type { Note } from '@/types/api';
import { UI_SCALE, popupMaxH } from '@/utils/uiScale';

// Every note starts on the first (yellow) shade; the rest of the palette is
// offered via the color-picker button so a note can be told apart on request,
// not by default.
const NOTE_COLORS = ['#fef3c7', '#dbeafe', '#dcfce7', '#fce7f3', '#ede9fe', '#ffe4e6'];
const DEFAULT_NOTE_COLOR = NOTE_COLORS[0];

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-SG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function CopyLabelIcon({ copied }: { copied: boolean }) {
  if (copied) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

// Closed vs. open shackle — deliberately different shapes (not just a color
// swap) so the two states read apart at a glance instead of both looking like
// a generic "lock" badge.
function LockIcon({ locked }: { locked: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      {locked ? <path d="M8 11V7a4 4 0 0 1 8 0v4" /> : <path d="M8 11V7a4 4 0 0 1 7.75-1.5" />}
    </svg>
  );
}

function PinIcon({ pinned }: { pinned: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
    </svg>
  );
}

function PaletteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
      <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
      <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
      <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </svg>
  );
}

function AddNoteSlot({ onAdd, disabled }: { onAdd: () => void; disabled?: boolean }) {
  if (disabled) return null;
  return (
    <button
      onClick={onAdd}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        width: '100%', padding: '6px', fontSize: 12, fontWeight: 600, color: '#94a3b8',
        background: 'transparent', border: '1px dashed #cbd5e1', borderRadius: 6, cursor: 'pointer',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#6366f1'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#6366f1'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#cbd5e1'; }}
    >
      + Add note
    </button>
  );
}

function NoteCard({
  note,
  canEdit,
  onChangeText,
  onToggleLock,
  onTogglePin,
  onChangeColor,
  onDelete,
}: {
  note: Note;
  canEdit: boolean;
  onChangeText: (id: number, text: string) => void;
  onToggleLock: (id: number) => void;
  onTogglePin: (id: number) => void;
  onChangeColor: (id: number, color: string) => void;
  onDelete: (id: number) => void;
}) {
  const [draft, setDraft] = useState(note.text);
  const [copied, setCopied] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [lockedNotice, setLockedNotice] = useState(false);
  const [showPalette, setShowPalette] = useState(false);

  // Follow the server's text (another user's edit, a re-fetch) — but never over what is being
  // typed here and has not been saved yet, which a re-fetch would otherwise replace with the
  // older text the server still holds.
  useEffect(() => { if (!hasUnsavedNoteText(note.id)) setDraft(note.text); }, [note.text, note.id]);

  // Saving is not this card's job (it would be lost with the card): every change goes up at
  // once and utils/noteSaves sends it.
  const handleTextChange = (value: string) => {
    setDraft(value);
    onChangeText(note.id, value);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(draft).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleCrossClick = () => {
    if (note.locked) {
      setLockedNotice(true);
      setTimeout(() => setLockedNotice(false), 2200);
    } else {
      setConfirmingDelete(true);
    }
  };

  return (
    <div
      style={{
        background: note.color || DEFAULT_NOTE_COLOR,
        borderRadius: 8,
        padding: '10px 10px 34px',
        boxShadow: note.pinned ? '0 0 0 2px #f59e0b, 0 2px 6px rgba(0,0,0,0.12)' : '0 2px 6px rgba(0,0,0,0.12)',
        position: 'relative',
        minHeight: 90,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 4 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => canEdit && onTogglePin(note.id)}
            disabled={!canEdit}
            title={!canEdit ? 'View only' : note.pinned ? 'Unpin note' : 'Pin note to top'}
            style={{
              background: note.pinned ? '#f59e0b' : 'rgba(255,255,255,0.6)',
              color: note.pinned ? 'white' : '#475569',
              border: 'none', borderRadius: 5, cursor: canEdit ? 'pointer' : 'not-allowed',
              opacity: canEdit ? 1 : 0.5,
              width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PinIcon pinned={note.pinned} />
          </button>
          <button
            onClick={() => canEdit && onToggleLock(note.id)}
            disabled={!canEdit}
            title={!canEdit ? 'View only' : note.locked ? 'Unlock note (allow deletion)' : 'Lock note (prevent deletion)'}
            style={{
              background: note.locked ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.6)',
              color: note.locked ? '#78350f' : '#475569',
              border: 'none', borderRadius: 5, cursor: canEdit ? 'pointer' : 'not-allowed',
              opacity: canEdit ? 1 : 0.5,
              width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <LockIcon locked={note.locked} />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 4, position: 'relative' }}>
          {canEdit && (
          <button
            onClick={() => setShowPalette(v => !v)}
            title="Change note color"
            style={{
              background: 'rgba(255,255,255,0.6)', color: '#475569',
              border: 'none', borderRadius: 5, cursor: 'pointer',
              width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PaletteIcon />
          </button>
          )}
          {showPalette && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 3 }} onClick={() => setShowPalette(false)} />
              <div style={{
                position: 'absolute', top: 28, right: 0, zIndex: 4,
                background: 'white', borderRadius: 8, padding: 6,
                boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                display: 'flex', gap: 5,
              }}>
                {NOTE_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => { onChangeColor(note.id, c); setShowPalette(false); }}
                    title={c}
                    style={{
                      width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer',
                      border: (note.color || DEFAULT_NOTE_COLOR) === c ? '2px solid #1e293b' : '1px solid rgba(0,0,0,0.15)',
                      padding: 0,
                    }}
                  />
                ))}
              </div>
            </>
          )}
          {canEdit && (
          <button
            onClick={handleCrossClick}
            title={note.locked ? 'Unlock first to delete' : 'Delete note'}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#64748b', fontSize: 15, lineHeight: 1, padding: '2px 4px', flexShrink: 0,
            }}
          >
            ✕
          </button>
          )}
        </div>
      </div>

      {(note.created_at || note.updated_at) && (
        <div style={{ fontSize: 10, color: 'rgba(30,41,59,0.6)', marginBottom: 6, lineHeight: 1.3 }}>
          {note.created_at && <span>Created {formatTimestamp(note.created_at)}</span>}
          {note.updated_at && note.updated_at !== note.created_at && (
            <span>{note.created_at ? ' · ' : ''}Edited {formatTimestamp(note.updated_at)}</span>
          )}
        </div>
      )}

      {lockedNotice && (
        <div style={{
          position: 'absolute', top: 34, left: 10, right: 10, zIndex: 2,
          background: '#1e293b', color: 'white', fontSize: 11, borderRadius: 5,
          padding: '6px 8px', boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        }}>
          Unlock first to delete.
        </div>
      )}

      <textarea
        value={draft}
        onChange={(e) => handleTextChange(e.target.value)}
        readOnly={!canEdit}
        placeholder="Type a note…"
        maxLength={NOTE_MAX_LENGTH}
        rows={3}
        style={{
          flex: 1, width: '100%', resize: canEdit ? 'vertical' : 'none', border: 'none', background: 'transparent',
          fontSize: 13, lineHeight: 1.4, color: '#1e293b', fontFamily: 'inherit', outline: 'none',
          minHeight: 44, cursor: canEdit ? 'text' : 'default',
        }}
      />

      {canEdit && draft.length >= NOTE_MAX_LENGTH - 200 && (
        <div style={{ fontSize: 10, textAlign: 'right', marginRight: 34, color: draft.length >= NOTE_MAX_LENGTH ? '#b91c1c' : 'rgba(30,41,59,0.6)' }}>
          {draft.length}/{NOTE_MAX_LENGTH}{draft.length >= NOTE_MAX_LENGTH ? ' — limit reached' : ''}
        </div>
      )}

      <button
        onClick={handleCopy}
        title="Copy note text"
        style={{
          position: 'absolute', bottom: 8, right: 8,
          background: copied ? '#22c55e' : 'rgba(255,255,255,0.7)',
          color: copied ? 'white' : '#475569',
          border: 'none', borderRadius: 5, cursor: 'pointer',
          width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <CopyLabelIcon copied={copied} />
      </button>

      {confirmingDelete && (
        <ConfirmDialog
          title="Delete this note?"
          message="This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => { setConfirmingDelete(false); onDelete(note.id); }}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
    </div>
  );
}

export function NotesModal({ onClose, liveUpdateTick }: { onClose: () => void; liveUpdateTick?: number }) {
  const { canEdit } = useSession();
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refetch = () => {
    apiClient.getNotes()
      .then((result) => setNotes(result))
      .catch(() => setError('Failed to load notes'));
  };

  useEffect(() => {
    let cancelled = false;
    apiClient.getNotes()
      .then((result) => { if (!cancelled) setNotes(result); })
      .catch(() => { if (!cancelled) setError('Failed to load notes'); });
    return () => { cancelled = true; };
  }, []);

  // Re-fetch whenever another user changes the board (lock/unlock, add, delete,
  // edit) — the same "server pushed a change, go re-read it" pattern the roster
  // canvas uses for snapshot_update, just without needing the payload itself.
  // Skips the tick's initial value so this doesn't double-fetch on mount.
  const isFirstTick = useRef(true);
  useEffect(() => {
    if (isFirstTick.current) { isFirstTick.current = false; return; }
    refetch();
  }, [liveUpdateTick]);

  // A write can 404 if this session forked (roster regenerate) since the notes
  // were last fetched — re-syncing from the server (now pointed at the current
  // session) recovers instead of leaving the board stuck showing stale notes.
  const handleWriteError = (message: string) => {
    setError(message);
    refetch();
  };

  const handleAdd = async (afterId: number | null) => {
    try {
      const note = await apiClient.createNote('', afterId);
      setNotes((prev) => {
        const list = prev ?? [];
        const idx = afterId === null ? -1 : list.findIndex((n) => n.id === afterId);
        if (idx === -1) return [...list, note];
        return [...list.slice(0, idx + 1), note, ...list.slice(idx + 1)];
      });
    } catch {
      handleWriteError('Failed to add note');
    }
  };

  // Closing the panel sends whatever text is still waiting rather than dropping it.
  useEffect(() => () => flushNoteTexts(), []);

  const handleChangeText = (id: number, text: string) => {
    setNotes((prev) => (prev ?? []).map((n) => (n.id === id ? { ...n, text } : n)));
    scheduleNoteText(id, text, handleTextSaveError);
  };

  // A refusal of the text itself. A 404 means the note is gone (deleted elsewhere, or the session
  // forked) — re-sync. Anything else leaves the user's text on screen: a re-fetch here would swap
  // it for the older text the server still holds, which is how a note used to "disappear".
  const handleTextSaveError = (err: NoteSaveError) => {
    if (err.status === 404) handleWriteError('This note no longer exists — the board was refreshed');
    else setError('Could not save the last change to a note — copy its text before closing');
  };

  const handleToggleLock = (id: number) => {
    const current = (notes ?? []).find((n) => n.id === id);
    if (!current) return;
    const nextLocked = !current.locked;
    setNotes((prev) => (prev ?? []).map((n) => (n.id === id ? { ...n, locked: nextLocked } : n)));
    apiClient.updateNote(id, { locked: nextLocked }).catch(() => handleWriteError('Failed to update lock state'));
  };

  // Pinned notes float to the top, same ordering the server applies — sorted
  // here too (stable, so ties keep their existing relative order) for instant
  // feedback instead of waiting on a re-fetch.
  const handleTogglePin = (id: number) => {
    const current = (notes ?? []).find((n) => n.id === id);
    if (!current) return;
    const nextPinned = !current.pinned;
    setNotes((prev) => {
      const updated = (prev ?? []).map((n) => (n.id === id ? { ...n, pinned: nextPinned } : n));
      return [...updated].sort((a, b) => (a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1));
    });
    apiClient.updateNote(id, { pinned: nextPinned }).catch(() => handleWriteError('Failed to update pin state'));
  };

  const handleChangeColor = (id: number, color: string) => {
    setNotes((prev) => (prev ?? []).map((n) => (n.id === id ? { ...n, color } : n)));
    apiClient.updateNote(id, { color }).catch(() => handleWriteError('Failed to update note color'));
  };

  const handleDelete = (id: number) => {
    dropNoteText(id);
    apiClient.deleteNote(id)
      .then(() => setNotes((prev) => (prev ?? []).filter((n) => n.id !== id)))
      .catch((err: Error) => {
        // A 404 here just means the note is already gone — someone else's delete
        // (or a stale double-tap of our own) beat this request there. The end
        // state the user wanted is already true, so drop it locally and move on
        // instead of raising a "locked" alarm that isn't what actually happened.
        if (err.message.includes('[404]')) {
          setNotes((prev) => (prev ?? []).filter((n) => n.id !== id));
          return;
        }
        // A real lock conflict is a 403 — everything else (network blip, 500) is
        // generic. Only the 403 case is actually "locked by someone else".
        const message = err.message.includes('[403]')
          ? 'Note is locked — unlock it first to delete'
          : 'Failed to delete note — try again';
        handleWriteError(message);
      });
  };

  // Portalled to document.body — opened from a button that portals into the
  // tab bar, this modal needs to render regardless of whether its own React
  // parent (HomeTab) sits inside the display:none wrapper App.tsx uses when
  // Settings is the active tab. "position: fixed" doesn't escape a
  // display:none ancestor, so without the portal this modal would be
  // invisible whenever it's opened from a tab other than Counter/Officers.
  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 4600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#f8fafc', borderRadius: 10, maxWidth: 480, width: '100%', minWidth: 0,
          zoom: UI_SCALE, maxHeight: popupMaxH(82), display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 20px', borderBottom: '2px solid #e2e8f0', flexShrink: 0, background: 'white',
          borderTopLeftRadius: 10, borderTopRightRadius: 10,
        }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>📝 Notes</h3>
          <button
            onClick={onClose}
            style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', lineHeight: 1, padding: '2px 6px', borderRadius: 4 }}
          >
            ✕
          </button>
        </div>

        {!canEdit && <ViewOnlyBanner compact />}

        <div style={{ overflowY: 'auto', padding: 16, flex: 1 }}>
          {error && (
            <p style={{ color: '#dc2626', fontSize: 12, marginTop: 0 }}>{error}</p>
          )}
          {notes === null ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', marginTop: 32 }}>Loading…</p>
          ) : notes.length === 0 ? (
            <div style={{ marginTop: 24 }}>
              <p style={{ color: '#94a3b8', textAlign: 'center' }}>
                {canEdit ? 'No notes yet — add one to share with everyone in this session.' : 'No notes yet.'}
              </p>
              <AddNoteSlot onAdd={() => handleAdd(null)} disabled={!canEdit} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {notes.map((note) => (
                <div key={note.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <NoteCard
                    note={note}
                    canEdit={canEdit}
                    onChangeText={handleChangeText}
                    onToggleLock={handleToggleLock}
                    onTogglePin={handleTogglePin}
                    onChangeColor={handleChangeColor}
                    onDelete={handleDelete}
                  />
                  <AddNoteSlot onAdd={() => handleAdd(note.id)} disabled={!canEdit} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
